import throttle from 'lodash.throttle';
import { WheelEvent, useEffect, useState } from 'react';

import useUpdateViewerPageState from './useUpdateViewerPageState';

interface Props {
  totalPage?: number;
  updateNextThumbnails?: (page: number) => void;
  updatePrevThumbnails?: (page: number) => void;
}

const useWheel = ({
  totalPage,
  updateNextThumbnails,
  updatePrevThumbnails,
}: Props) => {
  const { currentPage, updateCurrentPage } = useUpdateViewerPageState();

  const [value, setValue] = useState<'prev' | 'next' | null>(null);

  const handleWheel = throttle((e: WheelEvent<HTMLDivElement>) => {
    if (value) {
      return;
    }

    const { deltaY, deltaX } = e;

    if (Math.abs(deltaY) <= 10 && Math.abs(deltaX) <= 10) {
      return;
    }

    const isPrev = deltaY < 0 || deltaX < 0;
    const isNext = deltaY > 0 || deltaX > 0;

    if (isPrev) {
      setValue('prev');
    }

    if (isNext) {
      setValue('next');
    }
  }, 500);

  useEffect(() => {
    if (!value) {
      return;
    }

    const timeout = setTimeout(() => {
      if (value) {
        setValue(null);
      }
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [value]);

  useEffect(() => {
    if (!value) {
      return;
    }

    if (value === 'next') {
      if (currentPage === totalPage) {
        return;
      }
      updateCurrentPage(currentPage + 1);
      updateNextThumbnails?.(currentPage + 1);
    } else if (value === 'prev') {
      if (currentPage === 1) {
        return;
      }

      updatePrevThumbnails?.(currentPage - 1);
      updateCurrentPage(currentPage - 1);
    }
  }, [value]);

  return handleWheel;
};

export default useWheel;
